import { css, html } from "lit";
import { customElement, state } from 'lit/decorators.js';
import store from "../../../store/store";
import { connect } from 'pwa-helpers';
import { StepId } from "../shop-checkout.interface";
import { ShopCart } from "../../cart/shop-cart/interfaces/shop-cart.interface";
import { CheckoutStepsBase } from "../checkout-steps-base";
import "../shipping-address-checkout-step";
import "./shipping-method-national-checkout-step";
import "./payment-billing-national-checkout-step";
import { StoreState } from "../../../store/store.interface";
import { getCart } from "../../cart/shop-cart/store/shop-cart.selectors";

/**
 * An element to load national checkout steps and complete the checkout process.
 */
@customElement('mhecomm-national-checkout-steps')
export class NationalCheckoutSteps extends connect(store)(CheckoutStepsBase) {
  static styles = [
    css`
      .ft-agreements {
        line-height: 24px;
      }
    `,
  ];


  @state()
  private setCheckoutStep = true;

  stateChanged(state: StoreState) {
    this.updateCheckoutStepsState(state);
    this.handleApiFailures(state);
    this.initializeStepsWhenCartLoads();
  }

  // It's required to be used for Guest Checkouts, as the cart will be just loaded
  // when the user land on the checkout page. The national checkout load the cart
  // on the cart page.
  private initializeStepsWhenCartLoads() {
    if (getCart(store.getState() as StoreState) && this.setCheckoutStep) {
      this.setCheckoutStep = false;
      this.setCheckoutSteps()
      this.isLoading = false;
    };
  }
  
  override setStepOrderStepIdMap(){
    if (this.shippable) {
      this.setShippableStepOrder();
    } else {
      this.setDigitalOnlySetOrder();
    }
    this.isLoading = false;
  }

  setShippableStepOrder() {
    this.stepOrderStepIdMap['1'] = StepId.ShippingAddressStep;
    this.stepOrderStepIdMap['2'] = StepId.ShippingMethodStep;
    this.stepOrderStepIdMap['3'] = StepId.PaymentBillingAddressStep;
  }

  setDigitalOnlySetOrder() {
    this.stepOrderStepIdMap['1'] = StepId.PaymentBillingAddressStep;
  }

  override setCartCheckoutContext(cart: ShopCart){
    this.shippable = cart?.shippable || false;
  }

  override renderCheckoutSteps() {
    if (this.shippable) {
      return this.renderPhysicalProductCheckoutSteps();
    }
    return this.renderDigitalOnlyCheckoutSteps();
  }

  renderPhysicalProductCheckoutSteps(){
    return html`
      <mhecomm-shipping-address-checkout-step
        ?forceComplete="${this.forceComplete || this.bypassShippingAddress}"
        ?showSequenceOrder="${true}"
        order = 1
        activeStepValue = "${this.activeStepValue}"
        id=${StepId.ShippingAddressStep}
        stepName="Shipping Address" 
        nextStepButtonName="Continue to Shipping Method">
      </mhecomm-shipping-address-checkout-step>
      <mhecomm-shipping-method-national-checkout-step
        ?forceComplete="${this.forceComplete || (this.bypassShippingAddress && this.bypassShippingMethod)}"
        ?showSequenceOrder="${true}"
        order = 2
        activeStepValue = "${this.activeStepValue}"
        id=${StepId.ShippingMethodStep}
        stepName="Shipping Method"
        nextStepButtonName="Continue to Payment and Billing">
      </mhecomm-shipping-method-national-checkout-step>
      <mhecomm-payment-billing-national-checkout-step 
        ?forceComplete="${this.forceComplete}"
        ?showSequenceOrder="${true}"
        order = 3
        activeStepValue = "${this.activeStepValue}"
        id=${StepId.PaymentBillingAddressStep}
        stepName="Payment and Billing"
        nextStepButtonName="Review Order">
      </mhecomm-payment-billing-national-checkout-step>
    `;
  }

  renderDigitalOnlyCheckoutSteps(){
    return html`
      <mhecomm-payment-billing-national-checkout-step 
        ?forceComplete="${this.forceComplete}"
        id=${StepId.PaymentBillingAddressStep}
        stepName="Payment and Billing"
        nextStepButtonName="Review Order">
      </mhecomm-payment-billing-national-checkout-step>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-national-checkout-steps': NationalCheckoutSteps;
  }
}
